/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { executeGetDetectionsByLocationsRequest } from "api/common/detections.api";
import circleGreen from "assets/images/indicators/circle_green.png";
import circleRed from "assets/images/indicators/circle_red.png";
import circleYellow from "assets/images/indicators/circle_yellow.png";
import { CLASS_EMBARK_PASSENGERS } from "constants/class.constants";
import { FetchFunctionParams, makeApiCallWithUpdateTime } from "utils/common";
import { useAppDispatch } from "store";
import { useParams } from "react-router-dom";
import { findLocation } from "utils/search-tree";
import { DetectionsByLocationQuery } from "types";
import MarkerClustererTaxiRanking from "./MarkerClusterer";
import MainMap from "../../../components/map/Map";

const Map = (props: any) => {
    const { id } = props;
    const locationsAbortControllerRef = useRef<AbortController | null>(null);
    const { groupId, locationId } = useParams();
    const { startTime, endTime, selectedRange } = useSelector((state) => state.time);
    const { locationsTree, locations } = useSelector((state) => state.newLocation);
    const [processedLocations, setProcessedLocations] = React.useState<any[]>([]);
    const [locationsArr, setLocationsArr] = React.useState<any[]>([]);

    const [locationsResponse, setLocationsResponse] = React.useState<any[]>([]);
    const clusterStyles = [
        { height: 89, width: 89, url: circleGreen },
        { height: 89, width: 89, url: circleYellow },
        { height: 89, width: 89, url: circleRed },
    ];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const dispatch = useAppDispatch();
    const containerStyle = {
        height: "430px",
        backgroundColor: "#000",
    };

    const fetchData = async ({ startTime, endTime }: FetchFunctionParams) => {
        if (groupId) {
            await getDetectionsByLocations({
                classification: CLASS_EMBARK_PASSENGERS,
                fromTime: startTime,
                toTime: endTime,
            });
        } else if (locationId) {
            await getDetectionsByLocations({
                classification: CLASS_EMBARK_PASSENGERS,
                locationId,
                fromTime: startTime,
                toTime: endTime,
            });
        } else {
            await getDetectionsByLocations({
                classification: CLASS_EMBARK_PASSENGERS,
                fromTime: startTime,
                toTime: endTime,
            });
        }
    };
    async function getDetectionsByLocations(data?: DetectionsByLocationQuery) {
        setIsLoading(true);
        try {
            locationsAbortControllerRef.current = new AbortController();
            const { signal } = locationsAbortControllerRef.current;
            const response = await executeGetDetectionsByLocationsRequest(data, { signal });
            if (response && Array.isArray(response)) {
                setLocationsResponse(response);
                if (id === null || id === undefined) {
                    mapLocations(locations, response);
                } else {
                    const node = findLocation(locationsTree, id);
                    if (node) {
                        setLocationsArr([]);
                        searchLocations(node);
                    }
                }
            }
            // eslint-disable-next-line no-empty
        } catch (e) {
        } finally {
            setIsLoading(false);
        }
    }

    function mapLocations(lst: any[], response: any) {
        const mapped = lst.map((l) => {
            const counts = response.find((i: { locationId: any }) => i.locationId === l.id);
            return {
                ...l,
                lat: parseFloat(l.latitude),
                lng: parseFloat(l.longitude),
                address: l.address1,
                count: counts?.count || 0,
            };
        });
        setProcessedLocations(mapped);
    }

    function searchLocations(element: any): any {
        if (element && element.type === "location") {
            setLocationsArr((locationsArr) => [...locationsArr, element]);
        } else {
            let i;
            for (i = 0; i < element.children.length; i++) {
                searchLocations(element.children[i]);
            }
        }
    }

    const markerClustererCalculator = (markers: Array<any>): any => {
        if (markers.find((marker) => marker.count > 6)) return { index: 3, text: markers.length };
        if (markers.find((marker) => marker.count > 2)) return { index: 2, text: markers.length };
        return { index: 1, text: markers.length };
    };

    useEffect(() => {
        makeApiCallWithUpdateTime(selectedRange, { startTime, endTime }, fetchData, dispatch);

        return () => {
            locationsAbortControllerRef.current?.abort();
        };
    }, [groupId, locationId, startTime, endTime, id]);

    useEffect(() => {
        if (locationsArr === null) {
            setProcessedLocations([]);
        } else {
            mapLocations(locationsArr, locationsResponse);
        }
    }, [locationsArr]);

    return (
        <MainMap containerStyle={containerStyle} locations={processedLocations}>
            <MarkerClustererTaxiRanking
                markerClustererCalculator={markerClustererCalculator}
                clusterStyles={clusterStyles}
                locations={processedLocations}
            />
        </MainMap>
    );
};

export default memo(Map);
